import { useEffect, useState } from "react";
import { SITE_URL } from "@/config/site";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/login`
    : `${SITE_URL}/login`);

/**
 * Sign in with Apple — JS SDK flow.
 *
 * Activation requires 4 env vars on the backend (APPLE_CLIENT_ID,
 * APPLE_TEAM_ID, APPLE_KEY_ID, APPLE_PRIVATE_KEY) + REACT_APP_APPLE_CLIENT_ID
 * on the frontend. Setup steps live in `backend/routers/apple_auth.py`
 * docstring + project memory `project_apple_oauth_setup`.
 *
 * Without REACT_APP_APPLE_CLIENT_ID configured, the component returns
 * null so users don't see a broken option (previously it showed an
 * error toast on click — confusing UX).
 */
const AppleSignInButton = ({ mode = "signin" }) => {
  const { t } = useTranslation();
  const { appleLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !APPLE_CLIENT_ID) return;
    if (window.AppleID) {
      setSdkReady(true);
      return;
    }
    // Lazy-load only when the button mounts (saves 50KB on the landing).
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.onload = () => setSdkReady(true);
    document.body.appendChild(script);
    return () => {
      // Don't remove — the SDK is shared with the other button mount
      // (Login + Register pages). Cleaning would break a back-nav.
    };
  }, []);

  useEffect(() => {
    if (!sdkReady || !APPLE_CLIENT_ID) return;
    window.AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: "name email",
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });
  }, [sdkReady]);

  const handleClick = async () => {
    if (!sdkReady) {
      toast.error(
        t(
          "auth.apple.sdkNotReady",
          "Apple SDK pas encore prêt — réessayez dans 1s"
        )
      );
      return;
    }
    setLoading(true);
    try {
      const data = await window.AppleID.auth.signIn();
      // First-time sign-in only — Apple ships user.name once and never
      // again, forward it now or it's lost.
      const name = data?.user?.name
        ? `${data.user.name.firstName || ""} ${
            data.user.name.lastName || ""
          }`.trim()
        : null;
      await appleLogin(data.authorization.id_token, name);
      toast.success(
        mode === "signup"
          ? t("auth.apple.signupSuccess", "Inscription Apple réussie !")
          : t("auth.apple.signinSuccess", "Connexion Apple réussie !")
      );
      navigate("/dashboard");
    } catch (err) {
      // popup_closed_by_user is the common case when user backs out
      if (err?.error === "popup_closed_by_user") {
        // silent — user just changed their mind, no error toast
        return;
      }
      console.error("Apple login error:", err);
      toast.error(t("auth.apple.error", "Erreur de connexion Apple"));
    } finally {
      setLoading(false);
    }
  };

  if (!APPLE_CLIENT_ID) return null;

  const label =
    mode === "signup"
      ? t("auth.apple.signup", "S'inscrire avec Apple")
      : t("auth.apple.signin", "Continuer avec Apple");

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid="apple-signin-btn"
      aria-label={label}
      className="w-full h-10 rounded-full bg-black text-white border border-white/10 hover:bg-zinc-900 active:bg-zinc-950 transition-colors flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
    >
      {/* Apple logo SVG — official mark, single color, scales cleanly */}
      <svg
        className="w-4 h-4"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M17.05 12.04c-.03-3.02 2.47-4.47 2.58-4.54-1.41-2.06-3.6-2.34-4.38-2.37-1.86-.19-3.64 1.1-4.59 1.1-.96 0-2.41-1.07-3.97-1.04-2.04.03-3.93 1.18-4.99 3-2.13 3.69-.54 9.14 1.51 12.13 1.01 1.46 2.21 3.1 3.79 3.05 1.52-.06 2.09-.99 3.93-.99 1.83 0 2.36.99 3.97.96 1.65-.03 2.69-1.5 3.69-2.96 1.16-1.7 1.64-3.36 1.66-3.45-.04-.02-3.18-1.22-3.21-4.85zM14.04 3.35c.84-1.02 1.41-2.43 1.25-3.83-1.21.05-2.67.81-3.54 1.83-.78.9-1.46 2.34-1.28 3.71 1.34.1 2.72-.69 3.57-1.71z" />
      </svg>
      {loading ? "..." : label}
    </button>
  );
};

export default AppleSignInButton;
